/**
 * Custom Hook for Alert Management
 * Provides easy-to-use alert functionality throughout the application
 */

import React, { useState, useCallback } from 'react';
import Alert, { AlertProps } from '../components/common/Alert';

interface AlertOptions {
  title: string;
  message: string;
  type?: 'info' | 'success' | 'warning' | 'error';
  confirmText?: string;
  cancelText?: string;
}

interface ConfirmOptions extends AlertOptions {
  onConfirm: () => void;
}

export const useAlert = () => {
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    props: Partial<AlertProps>;
  }>({
    isOpen: false,
    props: {}
  });

  const closeAlert = useCallback(() => {
    setAlertState({ isOpen: false, props: {} });
  }, []);

  // Simple alert (like window.alert)
  const alert = useCallback((options: AlertOptions) => {
    setAlertState({
      isOpen: true,
      props: {
        ...options,
        onClose: closeAlert,
        showCancel: false
      }
    });
  }, [closeAlert]);

  // Confirmation dialog (like window.confirm)
  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setAlertState({
        isOpen: true,
        props: {
          ...options,
          onClose: () => {
            closeAlert();
            resolve(false);
          },
          onConfirm: () => {
            options.onConfirm();
            closeAlert();
            resolve(true);
          },
          showCancel: true,
          confirmText: options.confirmText || 'Confirm',
          cancelText: options.cancelText || 'Cancel'
        }
      });
    });
  }, [closeAlert]);

  // Convenience methods for different types
  const success = useCallback((title: string, message: string) => {
    alert({ title, message, type: 'success' });
  }, [alert]);

  const error = useCallback((title: string, message: string) => {
    alert({ title, message, type: 'error' });
  }, [alert]);

  const warning = useCallback((title: string, message: string) => {
    alert({ title, message, type: 'warning' });
  }, [alert]);

  const info = useCallback((title: string, message: string) => {
    alert({ title, message, type: 'info' });
  }, [alert]);

  // Confirmation dialogs with different types
  const confirmDelete = useCallback((itemName: string, onConfirm: () => void) => {
    return confirm({
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete ${itemName}? This action cannot be undone.`,
      type: 'error',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm
    });
  }, [confirm]);

  const confirmAction = useCallback((title: string, message: string, onConfirm: () => void) => {
    return confirm({
      title,
      message,
      type: 'warning',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm
    });
  }, [confirm]);

  // Component to render
  const AlertComponent = alertState.isOpen ? (
    <Alert
      isOpen={alertState.isOpen}
      onClose={alertState.props.onClose || closeAlert}
      onConfirm={alertState.props.onConfirm}
      title={alertState.props.title || ''}
      message={alertState.props.message || ''}
      type={alertState.props.type}
      confirmText={alertState.props.confirmText}
      cancelText={alertState.props.cancelText}
      showCancel={alertState.props.showCancel}
    />
  ) : null;

  return {
    alert,
    confirm,
    success,
    error,
    warning,
    info,
    confirmDelete,
    confirmAction,
    AlertComponent,
    closeAlert
  };
};

export default useAlert;
